/**
 * Unit Types for the Siege game
 *
 * All values are in normalized coordinates (0-1) unless otherwise specified.
 * Units spawn from edges and move toward the base at center (0.5, 0.5).
 */

/**
 * Unit type definitions
 * @type {Object.<string, UnitType>}
 */
export const UNIT_TYPES = {
    grunt: {
        id: 'grunt',
        name: 'Grunt',
        hp: 30,
        speed: 0.03,       // Units per second (normalized)
        damage: 5,         // Damage per second to base
        size: 0.02,        // Radius for hit detection
        color: '#ef4444',  // Red
        points: 10,
        description: 'Basic unit, balanced stats'
    },

    runner: {
        id: 'runner',
        name: 'Runner',
        hp: 15,
        speed: 0.06,       // Fast
        damage: 3,
        size: 0.015,       // Smaller
        color: '#f97316',  // Orange
        points: 15,
        description: 'Fast but fragile'
    },

    tank: {
        id: 'tank',
        name: 'Tank',
        hp: 100,
        speed: 0.015,      // Slow
        damage: 10,
        size: 0.035,       // Large
        color: '#8b5cf6',  // Purple
        points: 25,
        description: 'Slow and heavily armored'
    },

    splitter: {
        id: 'splitter',
        name: 'Splitter',
        hp: 40,
        speed: 0.025,
        damage: 4,
        size: 0.025,
        color: '#22c55e',  // Green
        points: 20,
        spawnOnDeath: { type: 'grunt', count: 2 },
        description: 'Spawns 2 grunts when destroyed'
    },

    healer: {
        id: 'healer',
        name: 'Healer',
        hp: 25,
        speed: 0.02,       // Slow
        damage: 2,
        size: 0.02,
        color: '#06b6d4',  // Cyan
        points: 30,
        healRadius: 0.08,  // Normalized radius
        healRate: 5,       // HP per second to nearby units
        description: 'Heals nearby units'
    },

    boss: {
        id: 'boss',
        name: 'Boss',
        hp: 500,
        speed: 0.008,      // Very slow
        damage: 25,
        size: 0.06,        // Very large
        color: '#dc2626',  // Dark red
        points: 100,
        spawnMinions: { type: 'grunt', interval: 3000, count: 2 },
        description: 'Massive unit that spawns minions'
    }
};

/**
 * Get unit type definition
 * @param {string} typeId - Unit type ID
 * @returns {UnitType|null}
 */
export function getUnitType(typeId) {
    return UNIT_TYPES[typeId] || null;
}

/**
 * Create a unit instance from a type
 * @param {string} typeId - Unit type ID
 * @param {number} x - Initial X position (normalized)
 * @param {number} y - Initial Y position (normalized)
 * @param {Object} overrides - Property overrides
 * @returns {Unit}
 */
export function createUnit(typeId, x, y, overrides = {}) {
    const type = getUnitType(typeId);
    if (!type) {
        console.warn(`[Siege] Unknown unit type: ${typeId}`);
        return null;
    }

    return {
        id: `unit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        typeId: type.id,
        x,
        y,
        hp: type.hp,
        maxHp: type.hp,
        speed: type.speed,
        damage: type.damage,
        size: type.size,
        color: type.color,
        points: type.points,

        // State
        isCapturing: false,
        isDead: false,
        spawnedAt: Date.now(),

        // Special properties
        spawnOnDeath: type.spawnOnDeath || null,
        spawnMinions: type.spawnMinions || null,
        healRadius: type.healRadius || 0,
        healRate: type.healRate || 0,
        lastMinionSpawn: 0,

        // Overrides
        ...overrides
    };
}

/**
 * Get all available unit type IDs
 * @returns {string[]}
 */
export function getUnitTypeIds() {
    return Object.keys(UNIT_TYPES);
}

/**
 * Calculate difficulty multiplier for unit stats
 * @param {string} difficulty - 'easy', 'normal', 'hard', 'nightmare'
 * @returns {Object} Multipliers for hp, speed, damage
 */
export function getDifficultyMultipliers(difficulty) {
    const multipliers = {
        easy: { hp: 0.7, speed: 0.8, damage: 0.7 },
        normal: { hp: 1.0, speed: 1.0, damage: 1.0 },
        hard: { hp: 1.3, speed: 1.2, damage: 1.3 },
        nightmare: { hp: 1.8, speed: 1.4, damage: 1.6 }
    };
    return multipliers[difficulty] || multipliers.normal;
}

/**
 * Apply difficulty scaling to a unit
 * @param {Unit} unit - Unit to scale
 * @param {string} difficulty - Difficulty level
 * @returns {Unit} Scaled unit
 */
export function applyDifficultyScaling(unit, difficulty) {
    const mult = getDifficultyMultipliers(difficulty);
    return {
        ...unit,
        hp: Math.round(unit.hp * mult.hp),
        maxHp: Math.round(unit.maxHp * mult.hp),
        speed: unit.speed * mult.speed,
        damage: unit.damage * mult.damage
    };
}
